import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, Radio, RefreshCw, Settings } from 'lucide-react';
import AppHeader from '../../components/common/AppHeader';
import ProcessTracker from '../../components/recruiting/ProcessTracker';
import Pagination from '../../components/ui/Pagination';
import useDebounce from '../../hooks/useDebounce';
import { jobConnectApi } from '../../services/api';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  ['PUBLISHED', 'Publiée'],
  ['DRAFT', 'Brouillon'],
  ['INACTIVE', 'Inactive'],
  ['EXPIRED', 'Expirée'],
  ['NOT_CREATED', 'Non diffusée'],
  ['DELETED', 'Supprimée'],
  ['UNKNOWN', 'Inconnue'],
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS);

const statusClasses = {
  PUBLISHED: 'bg-green-100 text-green-800',
  DRAFT: 'bg-blue-100 text-blue-800',
  INACTIVE: 'bg-gray-100 text-gray-700',
  EXPIRED: 'bg-amber-100 text-amber-900',
  NOT_CREATED: 'bg-slate-100 text-slate-700',
  DELETED: 'bg-red-100 text-red-800',
  UNKNOWN: 'bg-gray-100 text-gray-700',
};

const syncLabels = {
  IDLE: 'À synchroniser',
  PENDING: 'Synchronisation en attente',
  IN_PROGRESS: 'Synchronisation en cours',
  SUCCEEDED: 'Synchronisée',
  RETRY_SCHEDULED: 'Nouvel essai planifié',
  FAILED: 'Synchronisation échouée',
  DELETE_PENDING: 'Suppression en cours',
};

const formatDate = value => value
  ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
  : 'Date non renseignée';

const JobConnectPublicationsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [managedNeedId, setManagedNeedId] = useState(null);
  const requestId = useRef(0);
  const debouncedSearch = useDebounce(searchValue, 300);

  const loadPublications = useCallback(async () => {
    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    setLoading(true);
    setError(false);
    try {
      const params = { limit: PAGE_SIZE, skip: offset };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (status) params.status = status;
      const response = await jobConnectApi.listAllPublications(params);
      if (currentRequestId !== requestId.current) return;
      setItems(response.items || []);
      setTotal(response.total || 0);
    } catch {
      if (currentRequestId !== requestId.current) return;
      setError(true);
    } finally {
      if (currentRequestId === requestId.current) setLoading(false);
    }
  }, [debouncedSearch, offset, status]);

  useEffect(() => {
    loadPublications();
    return () => {
      requestId.current += 1;
    };
  }, [loadPublications]);

  const handleSearchChange = useCallback(value => {
    setSearchValue(value);
    setOffset(0);
  }, []);

  const handleStatusChange = event => {
    setStatus(event.target.value);
    setOffset(0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Rechercher un besoin ou une référence"
        searchAriaLabel="Rechercher une diffusion"
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-blue-700">JobConnect</p>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Diffusions</h1>
            <p className="mt-2 text-sm text-gray-600">Suivez les offres actuelles et leur historique de diffusion.</p>
          </div>
          <div className="w-full sm:w-56">
            <label htmlFor="jobconnect-status" className="mb-1 block text-sm font-medium text-gray-700">
              Statut de diffusion
            </label>
            <select
              id="jobconnect-status"
              value={status}
              onChange={handleStatusChange}
              className="min-h-[44px] w-full rounded-lg border-gray-300 bg-white text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Tous les statuts</option>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-5 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
          <p className="text-sm text-gray-600">Diffusions trouvées</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{loading ? '…' : total}</p>
        </div>

        {loading && (
          <div className="rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm" aria-live="polite">
            <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
            <p className="mt-3 text-sm text-gray-600">Chargement des diffusions…</p>
          </div>
        )}

        {!loading && error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-white px-6 py-12 text-center shadow-sm">
            <p className="font-semibold text-gray-900">Impossible de charger les diffusions JobConnect</p>
            <p className="mt-1 text-sm text-gray-600">Vérifiez votre connexion puis réessayez.</p>
            <button
              type="button"
              onClick={loadPublications}
              className="mt-4 min-h-[44px] rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-16 text-center shadow-sm">
            <Radio className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
            <h2 className="mt-3 text-lg font-semibold text-gray-900">Aucune diffusion JobConnect</h2>
            <p className="mt-1 text-sm text-gray-600">
              {searchValue || status ? 'Modifiez la recherche ou le filtre de statut.' : 'Les diffusions créées depuis un besoin apparaîtront ici.'}
            </p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="grid gap-4">
              {items.map(publication => (
                <article key={publication.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses[publication.effectiveStatus] || statusClasses.UNKNOWN}`}>
                          {STATUS_LABELS[publication.effectiveStatus] || publication.effectiveStatus}
                        </span>
                        {publication.credentialMode === 'TEST' && (
                          <span className="rounded-full bg-gray-900 px-2.5 py-1 text-xs font-semibold text-white">TEST</span>
                        )}
                      </div>
                      <h2 className="mt-3 truncate text-lg font-semibold text-gray-900">{publication.needTitle}</h2>
                      <p className="mt-1 text-sm text-gray-500">Référence {publication.reference}</p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[560px]">
                      <div className="rounded-lg bg-gray-50 px-4 py-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Candidatures</p>
                        <p className="mt-1 font-semibold text-gray-900">{publication.applicationCount} candidatures</p>
                        <p className="text-sm text-amber-700">{publication.pendingApplicationCount} à traiter</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 px-4 py-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Synchronisation</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{syncLabels[publication.syncState] || publication.syncState}</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 px-4 py-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Dernière mise à jour</p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{formatDate(publication.updatedAt)}</p>
                      </div>
                    </div>

                    <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:w-auto lg:flex-none lg:grid-cols-1 xl:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setManagedNeedId(publication.needId)}
                        disabled={managedNeedId === publication.needId}
                        aria-busy={managedNeedId === publication.needId}
                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-wait disabled:bg-blue-500"
                      >
                        {managedNeedId === publication.needId
                          ? <>Ouverture… <RefreshCw className="animate-spin" size={16} aria-hidden="true" /></>
                          : <>Gérer la diffusion <Settings size={16} aria-hidden="true" /></>}
                      </button>
                      {publication.publicUrl && (
                        <a
                          href={publication.publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          Voir l’offre <ExternalLink size={16} aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-5 overflow-hidden rounded-xl border border-gray-200">
              <Pagination
                limit={PAGE_SIZE}
                offset={offset}
                total={total}
                onNext={() => setOffset(current => current + PAGE_SIZE)}
                onPrevious={() => setOffset(current => Math.max(0, current - PAGE_SIZE))}
              />
            </div>
          </>
        )}
      </div>
      {managedNeedId && (
        <ProcessTracker
          needId={managedNeedId}
          isOpen
          initialWorkspace="diffusion"
          onClose={() => setManagedNeedId(null)}
        />
      )}
    </div>
  );
};

export default JobConnectPublicationsPage;
